// Copyright (c) StrangeBee 2023

import SortableItemList from '@Atoms/SortableItemList/SortableItemList';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THForm } from '@Molecules/THForm/THForm';
import { Empty, Form, FormInstance, Space } from 'antd';
import { useCallback, useMemo } from 'react';
import { sortColumns } from '../utils';
import { DescribeOutput, ObjectTypeV1 } from 'thehive-sdk';
import { FieldBlock } from './FieldBlock';

import './SortField.scss';

interface SortFieldProps {
    form: FormInstance;
    name?: string;
    entityType?: ObjectTypeV1;
    describeOutput?: DescribeOutput;
    title?: string;
    actionTitle: string;
}

export const SortField = ({ form, entityType, describeOutput, name = 'sort', title, actionTitle }: SortFieldProps) => {
    const i = useLocalMessage();
    const items = Form.useWatch<any[]>(name, form);

    /** available sort fields, we exclude already selected fields */
    const sortFields: string[] = useMemo(
        () =>
            entityType && describeOutput
                ? describeOutput?.[entityType]?.attributes
                      .reduce((acc: string[], attribute: { name: string }) => {
                          const selected = items ? items.map((it: { field: string }) => it.field) : [];

                          if (!selected.includes(attribute.name)) {
                              acc.push(attribute.name);
                          }
                          return acc;
                      }, [])
                      .sort(sortColumns)
                : [],
        [entityType, describeOutput, items],
    );

    const moveItem = useCallback(
        (newIndex: number, oldIndex: number) => {
            const element = items[oldIndex];
            items.splice(oldIndex, 1);
            items.splice(newIndex, 0, element);

            form.setFieldsValue({
                [name]: items,
            });
        },
        [form, items, name],
    );

    return (
        <THForm.List name={name}>
            {(fields, { add, remove }) => (
                <FieldBlock
                    title={title || i('general.sort')}
                    className="case-template-sort"
                    footerAction={
                        fields.length ? (
                            <THButton className="case-template-sort__add-btn" type="link" onClick={add}>
                                <Space align="center">
                                    <ICONS.PlusIcon />
                                    {actionTitle}
                                </Space>
                            </THButton>
                        ) : null
                    }
                >
                    {!fields.length && (
                        <Empty
                            imageStyle={{ height: 60 }}
                            description={
                                <div className="case-template-sort__none">
                                    <span>{i('report-template.no-sort')}</span>
                                    <THButton className="case-template-sort__add-btn-empty" type="link" onClick={add}>
                                        {actionTitle}
                                    </THButton>
                                </div>
                            }
                        />
                    )}

                    {fields.map((field, index) => (
                        <SortableItemList
                            dragType="case-template-sort"
                            index={index}
                            moveItem={moveItem}
                            key={field.key}
                        >
                            <THForm.Item key={field.name} noStyle>
                                <div className="case-template-sort-list-item">
                                    <div className="case-template-sort-list-item__title">
                                        <ICONS.MenuOutlined />
                                    </div>
                                    <THForm.Item
                                        name={[field.name, 'field']}
                                        key={field.key}
                                        className="case-template-sort-list-item__field"
                                        rules={[
                                            {
                                                required: true,
                                                message: i('common.required'),
                                            },
                                        ]}
                                    >
                                        <THSelect
                                            placeholder={i('report-template.sort.field')}
                                            options={sortFields.map((value) => ({
                                                label: i({ defaultMessage: value, id: `column.${value}` }),
                                                value,
                                            }))}
                                        />
                                    </THForm.Item>

                                    <THForm.Item
                                        name={[field.name, 'direction']}
                                        className="case-template-sort-list-item__direction"
                                        rules={[
                                            {
                                                required: true,
                                                message: i('common.required'),
                                            },
                                        ]}
                                    >
                                        <THSelect
                                            placeholder={i('report-template.sort.order')}
                                            options={['asc', 'desc'].map((value) => ({ label: value, value }))}
                                        />
                                    </THForm.Item>

                                    <THButton
                                        type="link"
                                        onClick={() => remove(field.name)}
                                        color="gray"
                                        className="case-template-sort-list-item__removeBtn"
                                    >
                                        <ICONS.CloseIcon />
                                    </THButton>
                                </div>
                            </THForm.Item>
                        </SortableItemList>
                    ))}
                </FieldBlock>
            )}
        </THForm.List>
    );
};
